/**
 * React binding for the i18n text catalog (Req 22.11).
 *
 * `I18nProvider` puts an active locale + catalog on the tree; `useMessages()`
 * exposes the resolver context and `useT()` returns the bound `t(id, params?)`
 * function components call to resolve every user-facing string by id.
 *
 * SSR-friendly by construction:
 *   - The provider holds plain data (locale tag + catalog) and derives the `t`
 *     function synchronously with `useMemo` — no effects, timers, or browser
 *     APIs — so it renders identically under `renderToString` and on hydration.
 *   - A default context value (the `en` catalog) means `useT()` works even
 *     without a surrounding provider, so a screen rendered in isolation (tests,
 *     or a not-yet-wrapped subtree) still resolves text rather than throwing.
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  makeT,
  type LocaleTag,
  type MessageCatalog,
  type TFunction,
} from "./catalog.js";
import { EN_MESSAGES } from "./messages.js";

/** The value carried by the i18n context. */
export interface I18nContextValue {
  /** The active locale tag (e.g. `"en"`). */
  readonly locale: LocaleTag;
  /** The active locale's catalog. */
  readonly catalog: MessageCatalog;
  /** The bound resolver: `t(id, params?)`. */
  readonly t: TFunction;
}

/**
 * Build a context value from a locale + catalog, memo-friendly. Exposed so a
 * host can construct the value once and reuse it.
 */
export function makeI18nContextValue(
  locale: LocaleTag,
  catalog: MessageCatalog,
): I18nContextValue {
  return { locale, catalog, t: makeT(catalog) };
}

/**
 * The default context: English. This makes `useT()` usable without a provider
 * (fail-safe), which keeps isolated renders and tests simple.
 */
const DEFAULT_I18N: I18nContextValue = makeI18nContextValue("en", EN_MESSAGES);

const I18nContext = createContext<I18nContextValue>(DEFAULT_I18N);

/** Props for {@link I18nProvider}. */
export interface I18nProviderProps {
  /** The active locale tag. Defaults to `"en"`. */
  readonly locale?: LocaleTag;
  /**
   * The active locale's catalog. Defaults to the English catalog. Passing a
   * regional-language catalog here swaps ALL resolved text without changing any
   * screen's structure or layout (Req 22.11).
   */
  readonly catalog?: MessageCatalog;
  readonly children: ReactNode;
}

/**
 * Provider that makes an active locale + catalog available to the tree. Derives
 * the `t` resolver synchronously (no effects) so it is safe in `renderToString`.
 */
export function I18nProvider({
  locale = "en",
  catalog = EN_MESSAGES,
  children,
}: I18nProviderProps): ReactElement {
  const value = useMemo<I18nContextValue>(
    () => makeI18nContextValue(locale, catalog),
    [locale, catalog],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Access the full i18n context (locale, catalog, and `t`). */
export function useMessages(): I18nContextValue {
  return useContext(I18nContext);
}

/**
 * The common case: get the bound resolver `t(id, params?)`. Components call
 * `const t = useT();` and then `t("auth.login.title")` — never a hardcoded
 * literal — satisfying Req 22.11 / Property 37.
 */
export function useT(): TFunction {
  return useContext(I18nContext).t;
}
